import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, ChevronDown, KeyRound, LogOut, Menu, Moon, Sun, User, UserCircle2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { ROLE_LABEL, type ChatMessage } from '@/lib/types'
import { fetchMyMentions, listUpcomingPromotions, type UpcomingPromotionRow } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { getStoredTheme, setTheme, type Theme } from '@/lib/theme'

/** Nút chuyển chế độ sáng/tối (B1). */
function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())
  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }
  return (
    <button
      onClick={toggle}
      className="rounded-md border bg-card p-2 hover:bg-accent"
      aria-label={theme === 'dark' ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
      title={theme === 'dark' ? 'Chế độ sáng' : 'Chế độ tối'}
    >
      {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  )
}

interface HeaderProps {
  onMenuToggle?: () => void
}

/** Chu kỳ tự làm mới cảnh báo nâng bậc (15 phút). */
const PROMO_REFRESH_MS = 15 * 60_000
/** Ngưỡng ngày để tính "sắp đến hạn" hiển thị trên chuông. */
const PROMO_DAYS_AHEAD = 30

function formatDateVN(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('vi-VN')
}

const MENTIONS_SEEN_KEY = 'v75-mentions-seen-at'

/**
 * Chuông thông báo hợp nhất (v0.26):
 *  - "Nhắc đến bạn" — tin nhắn @mention 7 ngày gần nhất (mọi vai trò);
 *    badge đếm mention MỚI kể từ lần mở chuông cuối (lưu localStorage).
 *  - "Nâng bậc" — cảnh báo TNVK trong 30 ngày (chỉ admin, từ v0.15).
 */
function NotificationBell({ canSeePromotions }: { canSeePromotions: boolean }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const userId = profile?.user.id ?? ''
  const [rows, setRows] = useState<UpcomingPromotionRow[]>([])
  const [mentions, setMentions] = useState<ChatMessage[]>([])
  const [seenAt, setSeenAt] = useState<string>(() => {
    try { return localStorage.getItem(MENTIONS_SEEN_KEY) ?? new Date(0).toISOString() }
    catch { return new Date(0).toISOString() }
  })
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const [promos, ms] = await Promise.all([
          canSeePromotions ? listUpcomingPromotions(PROMO_DAYS_AHEAD) : Promise.resolve([]),
          userId ? fetchMyMentions(userId) : Promise.resolve([]),
        ])
        if (mounted) { setRows(promos); setMentions(ms) }
      } catch {
        /* lỗi mạng tạm thời — giữ số liệu cũ, lần refresh sau sẽ cập nhật */
      }
    }
    load()
    const t = setInterval(load, PROMO_REFRESH_MS)
    return () => { mounted = false; clearInterval(t) }
  }, [canSeePromotions, userId])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const urgentCount = rows.filter(r => r.days_remaining <= 7).length
  const promoCount = rows.length
  const newMentions = mentions.filter(m => m.created_at > seenAt).length
  const total = promoCount + newMentions

  const onToggle = () => {
    setOpen(o => {
      const next = !o
      if (next && newMentions > 0) {
        const now = new Date().toISOString()
        setSeenAt(now)
        try { localStorage.setItem(MENTIONS_SEEN_KEY, now) } catch { /* bỏ qua */ }
      }
      return next
    })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onToggle}
        className="relative rounded-md border bg-card p-2 hover:bg-accent"
        aria-label={`Thông báo: ${newMentions} nhắc tên mới, ${promoCount} cảnh báo nâng bậc`}
        title="Thông báo"
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span
            className={
              'absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold text-white ' +
              (urgentCount > 0 || newMentions > 0 ? 'bg-red-600' : 'bg-amber-500')
            }
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-md border bg-popover shadow-lg">
          {/* ── Nhắc đến bạn ── */}
          <div className="border-b px-3 py-2 text-sm font-semibold">Nhắc đến bạn</div>
          {mentions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              Không có ai nhắc đến bạn trong 7 ngày qua.
            </div>
          ) : (
            <ul className="max-h-52 overflow-y-auto">
              {mentions.slice(0, 6).map(m => (
                <li key={m.id}>
                  <button
                    onClick={() => { setOpen(false); navigate('/chat') }}
                    className="flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent"
                  >
                    <span className="text-sm font-medium">{m.sender_name ?? '—'}</span>
                    <span className="truncate text-xs text-muted-foreground">{m.content}</span>
                    <span className="text-[10px] text-muted-foreground">{formatDateVN(m.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ── Nâng bậc (admin) ── */}
          {canSeePromotions && (
            <>
              <div className="border-b border-t px-3 py-2 text-sm font-semibold">
                Nâng bậc trong {PROMO_DAYS_AHEAD} ngày tới
                {urgentCount > 0 && (
                  <span className="ml-2 text-xs font-normal text-red-600">
                    {urgentCount} khẩn (≤7 ngày)
                  </span>
                )}
              </div>
              {promoCount === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">
                  Không có nhân viên nào đến hạn nâng bậc.
                </div>
              ) : (
                <ul className="max-h-52 overflow-y-auto">
                  {rows.slice(0, 6).map(r => (
                    <li key={r.employee_id} className="flex items-center justify-between gap-2 border-b px-3 py-2 last:border-b-0">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{r.ho_ten}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.ngach_code} bậc {r.bac} → {r.bac + 1} · hạn {formatDateVN(r.next_promotion_date)}
                        </div>
                      </div>
                      <Badge variant={r.days_remaining <= 7 ? 'destructive' : 'warning'}>
                        {r.days_remaining <= 0 ? 'Đến hạn' : `còn ${r.days_remaining} ngày`}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => { setOpen(false); navigate('/promotions') }}
                className="block w-full border-t px-3 py-2 text-center text-sm font-medium text-primary hover:bg-accent"
              >
                Mở trang Nâng bậc
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { profile, signOut, hasRole } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const canManagePromotions = hasRole('admin_luong', 'admin_he_thong')

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const onLogout = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-background/80 backdrop-blur px-4 md:px-6">
      <div className="flex items-center gap-3">
        {/* Hamburger — chỉ hiện trên mobile */}
        <button
          onClick={onMenuToggle}
          className="md:hidden rounded-md p-2 hover:bg-accent"
          aria-label="Mở menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      <div>
        <div className="text-sm text-muted-foreground">Xin chào</div>
        <div className="text-base font-semibold leading-tight">
          {profile?.employee?.ho_ten ?? '—'}{' '}
          <span className="text-xs font-normal text-muted-foreground">
            ({profile ? ROLE_LABEL[profile.user.role] : ''})
          </span>
        </div>
      </div>
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <NotificationBell canSeePromotions={canManagePromotions} />

        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-accent"
          >
            <UserCircle2 className="h-5 w-5 text-primary" />
            <span className="font-medium">{profile?.user.cccd}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-56 rounded-md border bg-popover shadow-lg">
              <div className="border-b px-3 py-2 text-xs text-muted-foreground">
                Phòng ban: {profile?.department?.name ?? '—'}
              </div>
              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
              >
                <User className="h-4 w-4" /> Thông tin cá nhân
              </Link>
              <Link
                to="/change-password"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
              >
                <KeyRound className="h-4 w-4" /> Đổi mật khẩu
              </Link>
              <button
                onClick={onLogout}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent"
              >
                <LogOut className="h-4 w-4" /> Đăng xuất
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
